import { SortableHeadCell, TextCell } from 'components/tables/cells';
import { createColumnHelper } from '@tanstack/table-core';
import { TextColumnProps } from 'components/tables/columnTypes';
import { AuditLogEvent } from './AuditLogEvent';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import LinkCell from 'components/tables/cells/LinkCell/LinkCell';
import { getProductConfig } from 'domain/product/productMap';
import { getEventTypeMapping } from './AuditLogEventType';

const columnHelper = createColumnHelper<AuditLogEvent>();

export const auditLogTableColumns = {
	message: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor(
			(row) => getEventTypeMapping(row.eventType).message,
			{
				id: 'message',
				enableSorting: true,
				//custom sorting function - sort by type,
				header: ({ header }) => (
					<SortableHeadCell
						text="Message"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue()}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
	account: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor((row) => row.accountName, {
			id: 'account',
			enableSorting: true,

			header: ({ header }) => (
				<SortableHeadCell
					text="Account"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => {
				const accountId = info.row.original.accountId;
				const accountName = info.row.original.accountName;

				return accountId && accountName ? (
					<LinkCell
						text={accountName}
						url={`/accounts/users/${accountId}`}
						align="flex-start"
					/>
				) : (
					<TextCell text={'-'} {...overrideProps?.cellProps} />
				);
			},
			...overrideProps?.columnDefProps,
		}),
	company: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor((row) => row.companyName, {
			id: 'company',
			enableSorting: true,

			header: ({ header }) => (
				<SortableHeadCell
					text="Company"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() || '-'}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	admin: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor((row) => row.adminId, {
			id: 'admin',
			enableSorting: false,
			header: ({ header }) => (
				<SortableHeadCell
					text="Performed by"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => {
				const adminId = info.row.original.adminId;
				const adminName = info.row.original.adminName;
				return adminId ? (
					<LinkCell
						text={`${adminName || 'View admin'} `}
						url={`/accounts/admin-users/${adminId}`}
						align="flex-start"
					/>
				) : (
					<TextCell text={'-'} {...overrideProps?.cellProps} />
				);
			},
			...overrideProps?.columnDefProps,
		}),
	application: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor(
			(row) => getProductConfig(row.applicationId ?? '')?.name,
			{
				id: 'application',
				enableSorting: true,
				header: ({ header }) => (
					<SortableHeadCell
						text="Application"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue() || '-'}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
	comment: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor((row) => row.comment, {
			id: 'Comment',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Comment"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() || '-'}
					withTooltip
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	time: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor((row) => row.timestamp, {
			id: 'time',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Time"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(info.getValue())}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	subscription: (overrideProps?: TextColumnProps<AuditLogEvent>) =>
		columnHelper.accessor((row) => row.subscriptionId?.toString(), {
			id: 'subscription',
			enableSorting: true,

			header: ({ header }) => (
				<SortableHeadCell
					text="Subscription"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => {
				const value = info.getValue();
				return value ? (
					<LinkCell
						text={'Show subscription'}
						url={`/subscriptions/${value}`}
						align="flex-start"
					/>
				) : (
					<TextCell text={'-'} {...overrideProps?.cellProps} />
				);
			},
			...overrideProps?.columnDefProps,
		}),
	details: (
		overrideProps?: TextColumnProps<AuditLogEvent>,
		onClick?: (data: any) => void,
	) =>
		columnHelper.accessor((row) => row.data, {
			id: 'details',
			enableSorting: true,

			header: ({ header }) => (
				<SortableHeadCell
					text="Details"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => {
				const eventType = info.row.original.eventType;
				const eventTypeMapping = getEventTypeMapping(eventType);
				const CellComponent = eventTypeMapping?.DetailsCellComponent;

				if (!CellComponent) {
					return <TextCell text={'-'} />;
				}

				const eventData = info.row.original.data;

				return <CellComponent data={eventData} onClick={onClick} />;
			},
			...overrideProps?.columnDefProps,
		}),
};
